import React from 'react';
import { injectIntl } from 'react-intl';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { RestoreDefaultsTextButton } from './RestoreDefaultsTextButton.tsx';
import type { ToggleSectionProps } from './types.ts';
import { i18n } from './i18n.ts';

const toggleSectionStyles = xcss({
	marginBottom: 'space.500',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleTextWrapper = styled.div({
	font: token('font.body'),
	color: token('color.text'),
	fontWeight: token('font.weight.medium'),
	marginRight: token('space.100'),
});

const toggleWrapperStyles = xcss({
	marginTop: 'space.negative.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BodyTextWrapper = styled.div({
	display: 'flex',
	font: token('font.body.small'),
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.medium'),
	marginBottom: token('space.100'),
});

const infoIconWrapperStyles = xcss({
	marginLeft: 'space.050',
});

export const ToggleSection = injectIntl(
	({
		spaceKey,
		testId,
		titleText,
		bodyText,
		underlinedBodyText,
		isChecked,
		isToggleDisabled,
		disabledTooltip,
		originalSuggestedStatesArray,
		setSuggestedStatesArray,
		onToggleClick,
		showRestoreDefaults,
		intl: { formatMessage },
	}: ToggleSectionProps) => {
		const isSuggestStatusesSection = !!underlinedBodyText;

		return (
			<Box
				testId="toggle-section-wrapper"
				// if it's the suggest statuses section and the toggle is checked, don't have margin bottom
				// since the customization area will help push everything down
				xcss={isSuggestStatusesSection && isChecked ? undefined : toggleSectionStyles}
			>
				<HeadingWrapper>
					<TitleTextWrapper>{titleText}</TitleTextWrapper>
					<Box xcss={toggleWrapperStyles}>
						{isToggleDisabled && disabledTooltip ? (
							<Tooltip content={disabledTooltip} position="top">
								<Toggle testId={testId} isChecked={isChecked} isDisabled onChange={onToggleClick} />
							</Tooltip>
						) : (
							<Toggle testId={testId} isChecked={isChecked} onChange={onToggleClick} />
						)}
					</Box>
				</HeadingWrapper>
				<BodyTextWrapper>
					{bodyText}
					{isSuggestStatusesSection &&
					setSuggestedStatesArray &&
					isChecked &&
					showRestoreDefaults &&
					originalSuggestedStatesArray ? (
						<RestoreDefaultsTextButton
							spaceKey={spaceKey}
							underlinedBodyText={underlinedBodyText}
							originalSuggestedStatesArray={originalSuggestedStatesArray}
							setSuggestedStatesArray={setSuggestedStatesArray}
						/>
					) : null}
					{isSuggestStatusesSection && (
						<Box xcss={infoIconWrapperStyles}>
							<Tooltip content={formatMessage(i18n.moreInfoMessage)} position="top">
								<StatusInformationIcon
									color={token('color.icon')}
									label="info-icon"
									testId="infoIcon"
								/>
							</Tooltip>
						</Box>
					)}
				</BodyTextWrapper>
			</Box>
		);
	},
);
